/* ==========================================================================
   Homellence – R1 (Runde 37) · Vorher/Nachher-Schieberegler

   Das Skript schreibt den Zustand an genau einer Stelle (`write`): die
   Zahl `--hl-ba-pos` (0 bis 100) und zwei Klassen für die Beschriftungen.
   Zuschnitt, Strich und Griff leitet das CSS aus der Zahl ab. Damit gibt
   es nur eine Wahrheit über den Zustand, und die steht am Element.

   Drei Wege führen zu dieser Zahl:

   1. Der echte `<input type="range">` über dem Bild. Er trägt Tastatur und
      Vorlesesoftware. Pfeiltasten, Bild auf/ab, Pos1/Ende macht der
      Browser selbst – dieses Skript hört nur zu und schreibt mit.
   2. Zeigegeräte (Maus, Finger, Stift) über Pointer Events auf dem Rahmen.
      Der Regler selbst darf die Geste nicht abfangen, deshalb wird die
      Position aus dem Rahmen gerechnet und an den Regler zurückgegeben.
   3. Der einmalige Hinweis beim ersten Sichtbarwerden.

   `touch-action: pan-y` steht im CSS an Rahmen und Regler: senkrechtes
   Scrollen bleibt am Handy frei, waagerechtes gehört dem Regler.
   ========================================================================== */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Der Hinweis wandert um diesen Betrag und wieder zurück. */
  var HINT_DELTA = 12;
  var HINT_MS = 1200;

  function clamp(value) {
    if (!isFinite(value)) return 50;
    if (value < 0) return 0;
    if (value > 100) return 100;
    return value;
  }

  class HlBeforeAfter extends HTMLElement {
    connectedCallback() {
      this.frame = this.querySelector('.hl-ba__frame');
      this.range = this.querySelector('.hl-ba__range');
      if (!this.frame || !this.range) return;

      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.onRangeInput = this.onRangeInput.bind(this);

      this.touched = false;
      this.dragging = false;
      this.hintFrame = null;

      var start = clamp(parseFloat(this.getAttribute('data-start')));
      this.write(start);

      /* Erst jetzt gilt der Baustein als bedienbar. Bis hierher zeigt das
         CSS den skriptlosen Rückfall (beide Bilder je zur Hälfte). */
      this.classList.add('hl-ba--ready');

      this.range.addEventListener('input', this.onRangeInput);
      this.range.addEventListener('change', this.onRangeInput);
      this.frame.addEventListener('pointerdown', this.onPointerDown);

      /* Tastaturbedienung zählt als Berührung: Danach kein Hinweis mehr. */
      this.range.addEventListener('keydown', this.markTouched.bind(this), { once: true });

      this.setupHint();
    }

    disconnectedCallback() {
      this.stopHint();
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
      if (this.range) {
        this.range.removeEventListener('input', this.onRangeInput);
        this.range.removeEventListener('change', this.onRangeInput);
      }
      if (this.frame) this.frame.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    /* ----------------------------------------------------------------
       Eine Stelle schreibt den Zustand
       ---------------------------------------------------------------- */

    write(value, syncRange) {
      var pos = clamp(value);
      /* Auf eine Nachkommastelle runden: Die Variable landet in
         `clip-path` und `left`, feinere Werte sieht niemand, kosten aber
         bei jeder Zeigerbewegung eine Neuberechnung. */
      this.pos = Math.round(pos * 10) / 10;
      this.style.setProperty('--hl-ba-pos', String(this.pos));

      /* Steht der Strich näher als 12 % an einer Ecke, weicht die
         Beschriftung dort. Zwei Klassen statt einer Rechnung im CSS:
         Chrome rechnet eine `opacity`, die von einer nicht angemeldeten
         Custom Property abhängt, beim Ziehen nicht neu (gemessen am
         17.09.). Klassen lösen den Übergang zuverlässig aus. */
      this.classList.toggle('hl-ba--near-left', this.pos < 12);
      this.classList.toggle('hl-ba--near-right', this.pos > 88);

      if (syncRange !== false && this.range) {
        var whole = String(Math.round(this.pos));
        if (this.range.value !== whole) this.range.value = whole;
      }
      if (this.range) {
        this.range.setAttribute('aria-valuetext', this.valueText(Math.round(this.pos)));
      }
    }

    /* Der Vorlesetext kommt aus der Sprachdatei und steht als Vorlage am
       Regler. `{{ value }}` wird hier ersetzt – so bleibt der Satzbau je
       Sprache in der Sprachdatei, nicht im Skript. */
    valueText(whole) {
      if (!this.template) {
        this.template = this.range.getAttribute('aria-valuetext') || '{{ value }} %';
        /* Die Vorlage kommt mit dem Startwert schon eingesetzt aus Liquid.
           Deshalb wird die erste gefundene Zahl wieder zum Platzhalter. */
        this.template = this.template.replace(/\d+([.,]\d+)?/, '{{ value }}');
      }
      return this.template.replace('{{ value }}', String(whole));
    }

    /* ----------------------------------------------------------------
       Weg 1 · Der echte Regler
       ---------------------------------------------------------------- */

    onRangeInput() {
      this.markTouched();
      this.write(parseFloat(this.range.value), false);
    }

    /* ----------------------------------------------------------------
       Weg 2 · Zeigegeräte

       Gerechnet wird aus dem Rahmen, nicht aus dem Regler: Ein
       `<input type="range">` setzt beim Klicken zwar selbst einen Wert,
       trifft die Stelle aber nur ungefähr, weil er den Griff mittig unter
       den Zeiger legen will. Beim Vorher/Nachher soll der Strich genau
       dort stehen, wo der Finger ist.
       ---------------------------------------------------------------- */

    fromEvent(event) {
      var rect = this.frame.getBoundingClientRect();
      if (!rect.width) return this.pos;
      return ((event.clientX - rect.left) / rect.width) * 100;
    }

    onPointerDown(event) {
      /* Nur die Hauptaste. Rechtsklick und Mittelklick gehören dem
         Browser. */
      if (event.button !== undefined && event.button !== 0) return;

      this.markTouched();
      this.dragging = true;
      this.write(this.fromEvent(event));

      /* Der Zeiger darf den Rahmen verlassen, ohne dass das Ziehen
         abreißt. Deshalb hängen die weiteren Ereignisse am Fenster. */
      window.addEventListener('pointermove', this.onPointerMove);
      window.addEventListener('pointerup', this.onPointerUp);
      window.addEventListener('pointercancel', this.onPointerUp);

      /* Beim Zeiger den Fokus auf den Regler legen: Wer mit der Maus
         anfasst, kann direkt mit den Pfeiltasten weitermachen. Bei
         Berührung nicht, sonst springt am Handy die Ansicht. */
      if (event.pointerType === 'mouse') {
        this.range.focus({ preventScroll: true });
      }
    }

    onPointerMove(event) {
      if (!this.dragging) return;
      /* Ein gehaltener Zeiger, der waagerecht wandert, ist ein Ziehen und
         kein Textmarkieren. */
      if (event.cancelable) event.preventDefault();
      this.write(this.fromEvent(event));
    }

    onPointerUp() {
      this.dragging = false;
      window.removeEventListener('pointermove', this.onPointerMove);
      window.removeEventListener('pointerup', this.onPointerUp);
      window.removeEventListener('pointercancel', this.onPointerUp);
    }

    /* ----------------------------------------------------------------
       Weg 3 · Der einmalige Hinweis
       ---------------------------------------------------------------- */

    markTouched() {
      this.touched = true;
      this.stopHint();
    }

    setupHint() {
      if (reduced.matches) return;
      if (!('IntersectionObserver' in window)) return;

      var self = this;
      this.observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            if (self.observer) {
              self.observer.disconnect();
              self.observer = null;
            }
            self.runHint();
          });
        },
        { threshold: 0.5 }
      );
      this.observer.observe(this);
    }

    /* Hin und zurück in einem `requestAnimationFrame`-Lauf. Bewusst nicht
       über eine CSS-Transition auf `--hl-ba-pos`: Eine Variable ohne
       `@property`-Anmeldung lässt sich nicht weich überblenden, sie
       springt. Mit eigener Rechnung bleibt die Bewegung überall gleich. */
    runHint() {
      if (this.touched || reduced.matches) return;

      var self = this;
      var from = this.pos;
      var to = clamp(from + HINT_DELTA);
      var begin = null;

      this.classList.add('hl-ba--hinting');

      var step = function (now) {
        if (self.touched) {
          self.stopHint();
          self.write(from);
          return;
        }
        if (begin === null) begin = now;

        var t = (now - begin) / HINT_MS;
        if (t >= 1) {
          self.write(from);
          self.stopHint();
          return;
        }

        /* Ein voller Sinusbogen: bei 0 und 1 am Ausgangspunkt, in der
           Mitte am weitesten. Weiches Anfahren und Abbremsen inklusive. */
        var eased = Math.sin(t * Math.PI);
        self.write(from + (to - from) * eased);
        self.hintFrame = requestAnimationFrame(step);
      };

      this.hintFrame = requestAnimationFrame(step);
    }

    stopHint() {
      if (this.hintFrame) {
        cancelAnimationFrame(this.hintFrame);
        this.hintFrame = null;
      }
      this.classList.remove('hl-ba--hinting');
    }
  }

  if (!customElements.get('hl-before-after')) {
    customElements.define('hl-before-after', HlBeforeAfter);
  }
})();
